import React, { useState, useEffect } from 'react'
import { Card, Dropdown, Form, Button, Alert, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { formatCurrency, formatDateOnly, highlightText } from '../utils/commonUtils'
import { useSearchWithDebounce } from '../hooks/useSearchWithDebounce'
import StockItemInfo from '../components/StockItemInfo'
import PaginationComponent from '../components/Pagination'
import ResizableTable from '../components/ResizableTable'

function RecentlyReceivedWidget({ onDataUpdate }) {
    // Default column visibility configuration for snapshot data
    const defaultColumnVisibility = {
        sku: true,
        image: true,
        gtin: true,
        description: true,
        lot: true,
        receivedDate: true,
        bbDate: false,
        warehouse: true,
        location: true,
        vendor: true,
        cost: false,
        totalCost: false,
        quantity: true
    }

    // Column labels for the toggle dropdown
    const columnLabels = {
        sku: 'SKU',
        image: 'Image',
        gtin: 'GTIN',
        description: 'Description',
        lot: 'LOT',
        receivedDate: 'Received Date',
        bbDate: 'Best Before',
        warehouse: 'Warehouse',
        location: 'Location',
        vendor: 'Vendor',
        cost: 'Cost',
        totalCost: 'Total Cost',
        quantity: 'Quantity'
    }

    // State for column visibility with localStorage persistence
    const [columnVisibility, setColumnVisibility] = useState(() => {
        const savedVisibility = localStorage.getItem('recentlyReceivedColumnVisibility')
        return savedVisibility ? JSON.parse(savedVisibility) : defaultColumnVisibility;
    })

    // Save column visibility to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('recentlyReceivedColumnVisibility', JSON.stringify(columnVisibility));
    }, [columnVisibility])

    // Column visibility toggle handler
    const toggleColumnVisibility = (column) => {
        setColumnVisibility(prevState => {
            const newState = {
                ...prevState,
                [column]: !prevState[column]
            };
            return newState;
        });
    }

    // Time window options
    const timeWindows = [
        { label: 'Last 24 Hours', days: 1 },
        { label: 'Last 2 Days', days: 2 },
        { label: 'Last 3 Days', days: 3 },
        { label: 'Last Week', days: 7 }
    ]

    // State for selected time window
    const [selectedWindow, setSelectedWindow] = useState(timeWindows[3]) // Default to 1 week

    // Search functionality with debouncing
    const { searchTerm, searchInputValue, handleSearchInputChange, clearSearch } = useSearchWithDebounce('')

    // Data fetching state
    const [snapshots, setSnapshots] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())

    // Fetch snapshots from API
    useEffect(() => {
        const fetchSnapshots = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await inventoryItemAPI.getRecentReceives(
                    selectedWindow.days,
                    currentPage,
                    pageSize,
                    searchTerm
                )

                if (response && response.results) {
                    setSnapshots(response.results.results || [])
                    setTotalItems(response.count || 0)
                }
            } catch (err) {
                console.error('Error fetching recent receives:', err)
                setError('Failed to load recent receives')
            } finally {
                setLoading(false)
            }
        }

        fetchSnapshots()
    }, [selectedWindow.days, pageSize, searchTerm, currentPage])

    // Reset to page 1 when search term or days changes
    useEffect(() => {
        setCurrentPage(1)
    }, [searchTerm, selectedWindow.days])

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    return (
        <Card className="h-100">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div style={{ position: 'relative', width: '250px' }}>
                        <Form.Control
                            type="text"
                            placeholder="Search inventory..."
                            value={searchInputValue}
                            onChange={handleSearchInputChange}
                            style={{ paddingRight: searchInputValue ? '30px' : '12px' }}
                            autoComplete="off"
                            spellCheck="false"
                        />
                        {searchInputValue && (
                            <Button
                                variant="link"
                                onClick={clearSearch}
                                title="Clear search"
                                style={{
                                    position: 'absolute',
                                    right: '2px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    padding: '0',
                                    width: '24px',
                                    height: '24px',
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#6c757d',
                                    fontSize: '20px',
                                    lineHeight: '1',
                                    cursor: 'pointer',
                                    textDecoration: 'none'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.color = '#000'}
                                onMouseOut={(e) => e.currentTarget.style.color = '#6c757d'}
                            >
                                ×
                            </Button>
                        )}
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <Dropdown>
                            <Dropdown.Toggle
                                variant="light"
                                id="column-toggle-dropdown"
                                style={{
                                    fontWeight: 'bold',
                                    padding: '0rem 0.5rem 0.35rem',
                                    height: '25px'
                                }}
                            >
                                Toggle Columns
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {Object.keys(columnVisibility).map((column) => (
                                    <Dropdown.Item
                                        key={column}
                                        as="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            toggleColumnVisibility(column);
                                        }}
                                    >
                                        <Form.Check
                                            type="checkbox"
                                            id={`column-toggle-${column}`}
                                            label={columnLabels[column]}
                                            checked={columnVisibility[column]}
                                            onChange={() => toggleColumnVisibility(column)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle
                                variant="light"
                                id="time-window-filter-dropdown"
                                style={{
                                    fontWeight: 'bold',
                                    padding: '0rem 0.5rem 0.35rem',
                                    height: '25px'
                                }}
                            >
                                {selectedWindow.label}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {timeWindows.map((window, index) => (
                                    <Dropdown.Item
                                        key={index}
                                        onClick={() => setSelectedWindow(window)}
                                        active={window.days === selectedWindow.days}
                                    >
                                        {window.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                ) : snapshots.length === 0 ? (
                    <Alert variant="info" className="m-3">
                        No items received in the selected time period
                    </Alert>
                ) : (
                    <div className="table-responsive">
                        <ResizableTable
                            tableId="recent_receives_table"
                            minHeight="350px"
                            columns={[
                                ...(columnVisibility.sku ? [{
                                    id: 'sku',
                                    header: 'SKU',
                                    cell: (row) => highlightText(row.stock_item?.sku || '', searchTerm)
                                }] : []),
                                ...(columnVisibility.image ? [{
                                    id: 'image',
                                    header: 'Image',
                                    cell: (row) => row.stock_item?.image ? (
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={
                                                <Tooltip className="stock-item-tooltip">
                                                    <StockItemInfo stockItem={row.stock_item} />
                                                </Tooltip>
                                            }
                                        >
                                            <img
                                                src={row.stock_item.image}
                                                alt="Stock Item Thumbnail"
                                                style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                                            />
                                        </OverlayTrigger>
                                    ) : null
                                }] : []),
                                ...(columnVisibility.gtin ? [{
                                    id: 'gtin',
                                    header: 'GTIN',
                                    cell: (row) => highlightText(row.stock_item?.gtin || '', searchTerm)
                                }] : []),
                                ...(columnVisibility.description ? [{
                                    id: 'description',
                                    header: 'Description',
                                    cell: (row) => highlightText(row.stock_item ? row.stock_item.description : 'Deleted Item', searchTerm)
                                }] : []),
                                ...(columnVisibility.lot ? [{
                                    id: 'lot',
                                    header: 'LOT',
                                    cell: (row) => highlightText(row.lot || '', searchTerm)
                                }] : []),
                                ...(columnVisibility.receivedDate ? [{
                                    id: 'receivedDate',
                                    header: 'Received Date',
                                    cell: (row) => formatDateOnly(row.received_datetime)
                                }] : []),
                                ...(columnVisibility.bbDate ? [{
                                    id: 'bbDate',
                                    header: 'Best Before',
                                    cell: (row) => formatDateOnly(row.bb_date)
                                }] : []),
                                ...(columnVisibility.warehouse ? [{
                                    id: 'warehouse',
                                    header: 'Warehouse',
                                    cell: (row) => highlightText(row.warehouse_name, searchTerm)
                                }] : []),
                                ...(columnVisibility.location ? [{
                                    id: 'location',
                                    header: 'Location',
                                    cell: (row) => highlightText(row.location_name, searchTerm)
                                }] : []),
                                ...(columnVisibility.vendor ? [{
                                    id: 'vendor',
                                    header: 'Vendor',
                                    cell: (row) => highlightText(row.vendor_name, searchTerm)
                                }] : []),
                                ...(columnVisibility.quantity ? [{
                                    id: 'quantity',
                                    header: 'Quantity',
                                    cell: (row) => row.quantity
                                }] : []),
                                ...(columnVisibility.cost ? [{
                                    id: 'cost',
                                    header: 'Cost',
                                    cell: (row) => formatCurrency(row.cost)
                                }] : []),
                                ...(columnVisibility.totalCost ? [{
                                    id: 'totalCost',
                                    header: 'Total Cost',
                                    cell: (row) => formatCurrency(row.total_cost)
                                }] : []),
                            ]}
                            data={snapshots}
                        />
                    </div>
                )}
                <PaginationComponent
                    currentPage={currentPage}
                    totalItems={totalItems}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={setPageSize}
                />
            </Card.Body>
        </Card>
    )
}

export default RecentlyReceivedWidget
